import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import { 
  Database, 
  RefreshCw, 
  AlertCircle,
  Download,
  Filter,
  TrendingUp,
  TrendingDown,
  Users,
  Grid3X3,
  Info,
  ChevronDown,
  X,
  CheckCircle,
  Settings
} from 'lucide-react';
import './CorrelationMatrix.css';
import { AnimatedCard, AnimatedNumber, useAnimateOnMount } from '../common/AnimationUtils';
import AILoadingOverlay from '../common/AILoadingOverlay';

// const API_BASE_URL = 'http://localhost:8000';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Cache key prefix for localStorage
const CACHE_KEY_PREFIX = 'dataquad_correlation_';

// Responsive hook
const useWindowWidth = () => {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return width;
};

const CorrelationMatrix = ({ 
  primaryData, 
  backcheckData, 
  projectId,  // NEW: Project ID for server-side correlation (Phase 2)
  primaryFileName = 'Primary Survey',
  backcheckFileName = 'Back-Check Data'
}) => {
  const { isDarkMode } = useTheme();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;
  const [activeDataset, setActiveDataset] = useState('primary');
  const [correlationData, setCorrelationData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCollector, setSelectedCollector] = useState('all');
  const [collectorColumn, setCollectorColumn] = useState('');
  const [correlationMethod, setCorrelationMethod] = useState('pearson');
  const [selectedCell, setSelectedCell] = useState(null);
  const [showSignificantOnly, setShowSignificantOnly] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);
  const [lastAnalyzedAt, setLastAnalyzedAt] = useState(null);
  const [showCollectorColumnSelector, setShowCollectorColumnSelector] = useState(false);

  // Current data based on toggle - define early so other hooks can use it
  const currentData = activeDataset === 'primary' ? primaryData : backcheckData;
  const currentFileName = activeDataset === 'primary' ? primaryFileName : backcheckFileName;

  // Get potential collector columns from data (any string or integer column could be a collector ID)
  const potentialCollectorColumns = useMemo(() => {
    if (!currentData?.column_types) return [];
    return Object.entries(currentData.column_types)
      .filter(([name, type]) => type === 'string' || type === 'integer')
      .map(([name]) => name);
  }, [currentData?.column_types]);

  // Cache utilities
  const getCacheKey = useCallback((dataset, fileName, collector, method) => {
    const safeFileName = fileName?.replace(/[^a-zA-Z0-9]/g, '_') || 'unknown';
    return `${CACHE_KEY_PREFIX}${dataset}_${safeFileName}_${collector}_${method}`;
  }, []);

  const getFromCache = useCallback((dataset, fileName, collector, method) => {
    try {
      const key = getCacheKey(dataset, fileName, collector, method);
      const cached = localStorage.getItem(key);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Check if cache is still valid (24 hours)
        if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Error reading correlation cache:', e);
    }
    return null;
  }, [getCacheKey]);

  const saveToCache = useCallback((dataset, fileName, collector, method, data) => {
    try {
      const key = getCacheKey(dataset, fileName, collector, method);
      const cacheData = {
        ...data,
        timestamp: Date.now(),
        analyzedAt: new Date().toISOString()
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (e) {
      console.warn('Error saving correlation to cache:', e);
    }
  }, [getCacheKey]);

  const clearCache = useCallback((dataset, fileName, collector, method) => {
    try {
      const key = getCacheKey(dataset, fileName, collector, method);
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('Error clearing correlation cache:', e);
    }
  }, [getCacheKey]);

  // Handle refresh button click
  const handleRefreshAnalysis = useCallback(() => {
    clearCache(activeDataset, currentFileName, selectedCollector, correlationMethod);
    setForceRefresh(prev => prev + 1);
  }, [activeDataset, currentFileName, selectedCollector, correlationMethod, clearCache]);

  // Color scheme
  const colors = {
    background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg: isDarkMode ? '#1a1a1dff' : '#FFFFFF',
    cardBgAlt: isDarkMode ? '#1a1a1dff' : '#FAFAFA',
    border: isDarkMode ? '#1F1F23' : '#E5E7EB',
    borderLight: isDarkMode ? '#1A1A1D' : '#F3F4F6',
    text: isDarkMode ? '#929292ff' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted: isDarkMode ? '#6B7280' : '#9CA3AF',
    headerBg: isDarkMode ? '#0D0E0E' : '#F9FAFB',
    rowHover: isDarkMode ? '#1A1A1D' : '#F9FAFB',
    rowAlt: isDarkMode ? '#2e2e36ff' : '#FAFAFA',
    accent: isDarkMode ? '#777777' : '#c7c7c7',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
  };

  // Auto-detect collector column
  useEffect(() => {
    if (currentData?.column_types) {
      const potentialCollectorColumns = Object.entries(currentData.column_types)
        .filter(([name, type]) => {
          const lowerName = name.toLowerCase();
          return (
            lowerName.includes('collector') ||
            lowerName.includes('enumerator') ||
            lowerName.includes('interviewer') ||
            lowerName.includes('surveyor') ||
            lowerName.includes('data_collector') ||
            lowerName.includes('datacollector')
          );
        })
        .map(([name]) => name);
      
      if (potentialCollectorColumns.length > 0) {
        setCollectorColumn(potentialCollectorColumns[0]);
      }
    }
  }, [currentData]);

  // Fetch correlation matrix (with caching)
  const fetchCorrelation = useCallback(async () => {
    const useServerSide = !!projectId;
    
    // Guard: need either projectId (for v2) or full_data/data (for v1)
    if (!useServerSide && (!currentData || (!currentData.full_data && !currentData.data) ||
        (currentData.full_data || currentData.data || []).length === 0)) {
      setCorrelationData(null);
      setLastAnalyzedAt(null);
      return;
    }
    if (useServerSide && !currentData) {
      setCorrelationData(null);
      setLastAnalyzedAt(null);
      return;
    }

    // Check cache first (unless forceRefresh was triggered)
    const cached = getFromCache(activeDataset, currentFileName, selectedCollector, correlationMethod);
    if (cached && forceRefresh === 0) {
      setCorrelationData(cached);
      setLastAnalyzedAt(cached.analyzedAt || null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      const token = localStorage.getItem('token');
      
      let response;
      
      if (useServerSide) {
        // V2: Server loads data from DB by project_id
        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (userInfo?.email) headers['user_email'] = userInfo.email;
        
        response = await fetch(`${API_BASE_URL}/correlation-matrix-v2/`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            project_id: projectId,
            data_file_type: activeDataset === 'primary' ? 'primary' : 'backcheck',
            collector_column: collectorColumn || null,
            collector_id: selectedCollector,
            method: correlationMethod,
            min_periods: 10
          })
        });
      } else {
        // V1 fallback: Send data in request body
        const dataToSend = currentData.full_data || currentData.data || [];
        response = await fetch(`${API_BASE_URL}/correlation-matrix/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'user_email': userInfo?.email || '',
          },
          body: JSON.stringify({
            data: dataToSend,
            column_types: currentData.column_types || {},
            collector_column: collectorColumn || null,
            collector_id: selectedCollector,
            method: correlationMethod,
            min_periods: 10
          })
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch correlation: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        saveToCache(activeDataset, currentFileName, selectedCollector, correlationMethod, result);
        setCorrelationData(result);
        setLastAnalyzedAt(new Date().toISOString());
      } else {
        setError(result.message || 'Failed to compute correlations');
      }
    } catch (err) {
      console.error('Error fetching correlation matrix:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
      if (forceRefresh > 0) setForceRefresh(0);
    }
  }, [currentData, activeDataset, currentFileName, collectorColumn, selectedCollector, correlationMethod, forceRefresh, projectId, getFromCache, saveToCache]);

  useEffect(() => {
    fetchCorrelation();
  }, [fetchCorrelation]);

  // Reset collector when dataset changes
  useEffect(() => {
    setSelectedCollector('all');
  }, [activeDataset]);

  // Get correlation color - matching Missing Data Heatmap color scheme
  // Red (#EF5350) for strong negative, Yellow (#FFF59D) for zero, Cyan/Teal (#26C6DA) for strong positive
  const getCorrelationColor = (value) => {
    if (value === null || value === undefined) return isDarkMode ? '#2A2A2E' : '#E5E7EB';
    
    // Normalize value from [-1, 1] to [0, 1]
    const normalized = (value + 1) / 2;
    
    // Color stops matching the Missing Data Heatmap gradient
    // -1 (strong negative) = Red: rgb(239, 83, 80) #EF5350
    // 0 (no correlation) = Light Yellow/Cream: rgb(255, 245, 157) #FFF59D
    // +1 (strong positive) = Cyan/Teal: rgb(38, 198, 218) #26C6DA
    
    let r, g, b;
    
    if (normalized <= 0.5) {
      // Interpolate from Red to Yellow (negative to zero correlation)
      const t = normalized * 2; // 0 to 1
      r = Math.round(239 + (255 - 239) * t);  // 239 -> 255
      g = Math.round(83 + (245 - 83) * t);    // 83 -> 245
      b = Math.round(80 + (157 - 80) * t);    // 80 -> 157
    } else {
      // Interpolate from Yellow to Cyan (zero to positive correlation)
      const t = (normalized - 0.5) * 2; // 0 to 1
      r = Math.round(255 + (38 - 255) * t);   // 255 -> 38
      g = Math.round(245 + (198 - 245) * t);  // 245 -> 198
      b = Math.round(157 + (218 - 157) * t);  // 157 -> 218
    }
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Get text color for cell - ensure readability on colored backgrounds
  const getTextColor = (value) => {
    if (value === null || value === undefined) return colors.textMuted;
    const absValue = Math.abs(value);
    // Dark text for lighter middle colors, white for strong correlations
    return absValue > 0.4 ? '#FFFFFF' : '#1F2937';
  };

  // Format correlation value
  const formatCorrelation = (value) => {
    if (value === null || value === undefined) return '—';
    return value.toFixed(2);
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!correlationData?.correlation_matrix) return;

    const { columns, correlation_matrix } = correlationData;
    
    // Header row
    let csv = ',' + columns.join(',') + '\n';
    
    // Data rows
    columns.forEach((col, i) => {
      const row = [col, ...correlation_matrix[i].map(v => v?.toFixed(4) ?? '')];
      csv += row.join(',') + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `correlation_matrix_${activeDataset}_${selectedCollector}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Render empty state
  if (!primaryData && !backcheckData) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '48px',
        textAlign: 'center',
        color: colors.textSecondary,
        backgroundColor: colors.background,
        marginTop: '40px'
      }}>
        <Database size={64} style={{ marginBottom: '24px', opacity: 0.5 }} />
        <h2 style={{ fontSize: '20px', marginBottom: '10px', color: colors.text }}>
          No Data Loaded
        </h2>
        <p style={{ fontSize: '13px', maxWidth: '400px' }}>
          Load your data to view the correlation matrix between numeric variables.
        </p>
      </div>
    );
  }

  return (
    <div className="correlation-matrix-container" style={{
      backgroundColor: colors.background,
      minHeight: '100%', 
      width: '100%',
      maxWidth: '100%',
      padding: isMobile ? '24px 12px 24px 12px' : '40px 60px 24px 60px',
      // marginLeft: '70px',
    }}>
      <AILoadingOverlay
        isVisible={isLoading}
        context="quality_analysis"
        title="Computing Correlations"
        subtitle="Analyzing variable relationships in your dataset"
        realTimeStats={{
          totalRows: currentData?.total_rows || currentData?.full_data?.length || currentData?.data?.length || 0,
          columnsAnalyzed: currentData?.column_types 
            ? Object.keys(currentData.column_types).length 
            : (currentData?.total_variables || currentData?.columns?.length || 0),
        }}
        showDetailedLogs={true}
      />
      {/* Header Section - Matching DataSummary */}
      <div className="ds-header" style={{ marginBottom: isMobile ? '16px' : '24px' }}>
        <div className="ds-header-top">
          <div>
            <h1 className="ds-title" style={{
              fontSize: '22px',
              fontWeight: '500',
              color: colors.text,
              margin: '0 0 4px 0',
              letterSpacing: '-0.5px'
            }}>
              Correlation Matrix
            </h1>
            <p className="ds-subtitle" style={{
              fontSize: '12px',
              color: colors.textSecondary,
              margin: 0,
              opacity: 0.65
            }}>
              Analyze relationships between numeric variables
            </p>
          </div>

          {/* Dataset Toggle - cd-dataset-tabs pattern */}
          <div className="ds-tabs-container" style={{ marginTop: '16px' }}>
            <button
              onClick={() => setActiveDataset('primary')}
              disabled={!primaryData}
              className={`ds-tab ${activeDataset === 'primary' ? 'active' : ''}`}
              style={{ color: colors.text }}
            >
              <Database size={13} />
              <span>Primary Survey</span>
              {primaryData && (
                <span className="ds-tab-count">
                  {primaryData.total_rows?.toLocaleString()} rows
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveDataset('backcheck')}
              disabled={!backcheckData}
              className={`ds-tab ${activeDataset === 'backcheck' ? 'active' : ''}`}
              style={{ color: colors.text }}
            >
              <CheckCircle size={13} />
              <span>Back-Check</span>
              {backcheckData && (
                <span className="ds-tab-count">
                  {backcheckData.total_rows?.toLocaleString()} rows
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Controls Row */}
      <AnimatedCard delay={0} translateY={12}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        {/* Left Controls */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Collector Column Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Settings size={16} style={{ color: colors.textSecondary }} />
            <select
              value={collectorColumn}
              onChange={(e) => {
                setCollectorColumn(e.target.value);
                setSelectedCollector('all'); // Reset collector selection when column changes
              }}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.cardBg,
                color: colors.text,
                fontSize: '14px',
                cursor: 'pointer',
                outline: 'none',
                minWidth: '160px'
              }}
            >
              <option value="">No collector column</option>
              {potentialCollectorColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </select>
          </div>

          {/* Collector Filter - only show if collector column is selected */}
          {collectorColumn && correlationData?.available_collectors?.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={16} style={{ color: colors.textSecondary }} />
              <select
                value={selectedCollector}
                onChange={(e) => setSelectedCollector(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.cardBg,
                  color: colors.text,
                  fontSize: '14px',
                  cursor: 'pointer',
                  outline: 'none',
                  minWidth: '180px'
                }}
              >
                <option value="all">All Collectors ({correlationData.available_collectors.length})</option>
                {correlationData.available_collectors.map(collector => (
                  <option key={collector} value={collector}>{collector}</option>
                ))}
              </select>
            </div>
          )}

          {/* Correlation Method */}
          <select
            value={correlationMethod}
            onChange={(e) => setCorrelationMethod(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.cardBg,
              color: colors.text,
              fontSize: '14px',
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            <option value="pearson">Pearson</option>
            <option value="spearman">Spearman</option>
            <option value="kendall">Kendall</option>
          </select>
        </div>

        {/* Right Controls */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Last analyzed indicator */}
          {lastAnalyzedAt && !isLoading && (
            <span style={{ 
              fontSize: '12px', 
              color: colors.textMuted,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <CheckCircle size={12} style={{ color: colors.success }} />
              Cached: {new Date(lastAnalyzedAt).toLocaleString(undefined, { 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </span>
          )}

          <button
            onClick={handleRefreshAnalysis}
            disabled={isLoading}
            title="Re-analyze correlations (clears cached results)"
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.cardBg,
              color: colors.text,
              fontSize: '14px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: isLoading ? 0.5 : 1
            }}
          >
            <RefreshCw size={16} className={isLoading ? 'spin' : ''} />
            Refresh
          </button>
          
          <button
            onClick={exportToCSV}
            disabled={!correlationData?.correlation_matrix}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.cardBg,
              color: colors.text,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: correlationData?.correlation_matrix ? 1 : 0.5
            }}
          >
            <Download size={16} />
            Export
          </button>
        </div>
      </div>
      </AnimatedCard>

      {/* Loading State */}
      {isLoading && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px',
          color: colors.textSecondary
        }}>
          <RefreshCw size={24} className="spin" style={{ marginRight: '12px' }} />
          Computing correlations...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: isDarkMode ? '#1F1515' : '#FEE2E2',
          borderRadius: '8px',
          color: colors.error,
          marginBottom: '16px',
          border: `1px solid ${isDarkMode ? '#3D2020' : '#FECACA'}`
        }}>
          <AlertCircle size={16} style={{ marginRight: '8px', display: 'inline' }} />
          {error}
        </div>
      )}

      {/* Main Content */}
      {!isLoading && correlationData && (
        <AnimatedCard delay={80} translateY={12}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 350px', gap: '20px' }}>
          {/* Correlation Matrix Heatmap */}
          <div style={{
            backgroundColor: colors.cardBg,
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            overflow: 'hidden',
            
          }}>
            <div style={{
              padding: isMobile ? '12px 14px' : '16px 20px',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? '10px' : '0'
            }}>
              <div>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, margin: 0 }}>
                  <Grid3X3 size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Correlation Heatmap
                </h3>
                <p style={{ fontSize: '12px', color: colors.textSecondary, margin: '4px 0 0 0' }}>
                  {correlationData.column_count} variables • {correlationData.observation_count?.toLocaleString()} observations
                  {selectedCollector !== 'all' && ` • Collector: ${selectedCollector}`}
                </p>
              </div>
              {/* Color Legend */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
                <span style={{ color: '#EF5350', fontWeight: '600' }}>-1</span>
                <div style={{
                  width: '120px',
                  height: '14px',
                  borderRadius: '7px',
                  background: 'linear-gradient(to right, #EF5350, #FFAB91, #FFF59D, #80DEEA, #26C6DA)',
                  border: `1px solid ${colors.border}`
                }} />
                <span style={{ color: '#26C6DA', fontWeight: '600' }}>+1</span>
              </div>
            </div>
            
            <div style={{ 
              overflowX: 'auto', 
              overflowY: 'auto', 
              maxHeight: 'calc(100vh)',
              height: 'auto',
              padding: '16px 16px 16px 0'
            }}>
              {correlationData.columns && correlationData.columns.length > 0 ? (
                <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: '11px' }}>
                  <thead>
                    <tr>
                      <th style={{ 
                        padding: '8px', 
                        position: 'sticky', 
                        left: 0, 
                        top: 0, 
                        zIndex: 20,
                        backgroundColor: colors.cardBg,
                        minWidth: '120px',
                        boxShadow: '2px 0 4px rgba(0,0,0,0.06)'
                      }}></th>
                      {correlationData.columns.map((col, idx) => (
                        <th 
                          key={idx}
                          style={{
                            padding: '8px 4px',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            backgroundColor: colors.cardBg,
                            fontWeight: '500',
                            color: colors.textSecondary,
                            whiteSpace: 'nowrap',
                            maxWidth: '80px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            transform: 'rotate(-45deg)',
                            transformOrigin: 'left bottom',
                            height: '80px',
                            verticalAlign: 'bottom'
                          }}
                          title={col}
                        >
                          {col.length > 12 ? col.substring(0, 10) + '...' : col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {correlationData.columns.map((rowCol, rowIdx) => (
                      <tr key={rowIdx}>
                        <td style={{
                          padding: '8px',
                          position: 'sticky',
                          left: 0,
                          zIndex: 5,
                          backgroundColor: colors.cardBg,
                          fontWeight: '500',
                          color: colors.textSecondary,
                          whiteSpace: 'nowrap',
                          minWidth: '120px',
                          maxWidth: '140px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          boxShadow: '2px 0 4px rgba(0,0,0,0.06)'
                        }} title={rowCol}>
                          {rowCol.length > 15 ? rowCol.substring(0, 13) + '...' : rowCol}
                        </td>
                        {correlationData.correlation_matrix[rowIdx].map((value, colIdx) => {
                          const isSelected = selectedCell?.row === rowIdx && selectedCell?.col === colIdx;
                          const isDiagonal = rowIdx === colIdx;
                          
                          return (
                            <td
                              key={colIdx}
                              onClick={() => {
                                if (!isDiagonal && value !== null) {
                                  setSelectedCell({ 
                                    row: rowIdx, 
                                    col: colIdx,
                                    var1: correlationData.columns[rowIdx],
                                    var2: correlationData.columns[colIdx],
                                    value: value
                                  });
                                }
                              }}
                              style={{
                                padding: '4px',
                                minWidth: '45px',
                                height: '35px',
                                textAlign: 'center',
                                backgroundColor: isDiagonal ? colors.border : getCorrelationColor(value),
                                color: getTextColor(value),
                                fontWeight: Math.abs(value) >= 0.7 ? '600' : '400',
                                cursor: isDiagonal ? 'default' : 'pointer',
                                border: isSelected ? '2px solid #FFFFFF' : '1px solid ' + colors.borderLight,
                                transition: 'all 0.15s ease',
                                boxShadow: isSelected ? '0 0 0 2px ' + colors.accent : 'none'
                              }}
                              title={isDiagonal ? `${rowCol} (1.00)` : `${correlationData.columns[rowIdx]} × ${correlationData.columns[colIdx]}: ${formatCorrelation(value)}`}
                            >
                              {isDiagonal ? '1' : formatCorrelation(value)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  color: colors.textSecondary 
                }}>
                  No numeric columns available for correlation analysis.
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Significant Correlations */}
          <div style={{
            backgroundColor: colors.cardBg,
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: isMobile ? '400px' : 'calc(100vh - 300px)'
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: `1px solid ${colors.border}`
            }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, margin: 0 }}>
                Notable Correlations
              </h3>
              <p style={{ fontSize: '12px', color: colors.textSecondary, margin: '4px 0 0 0' }}>
                Relationships with |r| ≥ 0.3
              </p>
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
              {correlationData.significant_correlations?.length > 0 ? (
                correlationData.significant_correlations.map((corr, idx) => (
                  <div
                    key={idx}
                    onClick={() => {
                      const rowIdx = correlationData.columns.indexOf(corr.var1);
                      const colIdx = correlationData.columns.indexOf(corr.var2);
                      setSelectedCell({
                        row: rowIdx,
                        col: colIdx,
                        var1: corr.var1,
                        var2: corr.var2,
                        value: corr.correlation
                      });
                    }}
                    style={{
                      padding: '12px',
                      marginBottom: '8px',
                      backgroundColor: colors.cardBgAlt,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: `1px solid ${colors.borderLight}`,
                      transition: 'all 0.15s ease'
                    }}
                    className="correlation-item"
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: corr.direction === 'positive' ? '#26C6DA' : '#EF5350'
                      }}>
                        {corr.correlation > 0 ? '+' : ''}{corr.correlation.toFixed(2)}
                      </span>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: '500',
                        backgroundColor: corr.direction === 'positive' 
                          ? 'rgba(38, 198, 218, 0.15)' 
                          : 'rgba(239, 83, 80, 0.15)',
                        color: corr.direction === 'positive' ? '#26C6DA' : '#EF5350'
                      }}>
                        {corr.strength}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: colors.text }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {corr.direction === 'positive' 
                          ? <TrendingUp size={12} style={{ color: '#26C6DA' }} />
                          : <TrendingDown size={12} style={{ color: '#EF5350' }} />
                        }
                        <span style={{ 
                          maxWidth: '130px', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }} title={corr.var1}>
                          {corr.var1}
                        </span>
                      </div>
                      <div style={{ 
                        fontSize: '11px', 
                        color: colors.textSecondary, 
                        marginLeft: '18px',
                        marginTop: '2px',
                        maxWidth: '130px', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }} title={corr.var2}>
                        × {corr.var2}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ 
                  padding: '24px', 
                  textAlign: 'center', 
                  color: colors.textSecondary,
                  fontSize: '13px'
                }}>
                  <Info size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                  <p>No significant correlations found (|r| ≥ 0.3)</p>
                </div>
              )}
            </div>

            {/* Selected Correlation Detail */}
            {selectedCell && selectedCell.value !== null && (
              <div style={{
                padding: '16px',
                borderTop: `1px solid ${colors.border}`,
                backgroundColor: colors.headerBg
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', color: colors.textSecondary }}>Selected</span>
                  <button
                    onClick={() => setSelectedCell(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      color: colors.textSecondary
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
                <div style={{ fontSize: '11px', color: colors.text, marginBottom: '4px' }}>
                  <strong>{selectedCell.var1}</strong>
                </div>
                <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '8px' }}>
                  × {selectedCell.var2}
                </div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: selectedCell.value > 0 ? '#26C6DA' : '#EF5350'
                }}>
                  {selectedCell.value > 0 ? '+' : ''}{selectedCell.value.toFixed(4)}
                </div>
              </div>
            )}
          </div>
        </div>
        </AnimatedCard>
      )}

      {/* Interpretation Guide */}
      {!isLoading && correlationData && (
        <AnimatedCard delay={160} translateY={12}>
        <div style={{
          marginTop: '20px',
          padding: '16px 20px',
          backgroundColor: colors.cardBg,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`
        }}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', color: colors.text, marginBottom: '12px' }}>
            <Info size={14} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Interpretation Guide
          </h4>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
            gap: '16px',
            fontSize: '12px'
          }}>
            <div>
              <span style={{ color: colors.textSecondary }}>Very Strong: </span>
              <span style={{ color: colors.text }}>|r| ≥ 0.8</span>
            </div>
            <div>
              <span style={{ color: colors.textSecondary }}>Strong: </span>
              <span style={{ color: colors.text }}>0.6 ≤ |r| &lt; 0.8</span>
            </div>
            <div>
              <span style={{ color: colors.textSecondary }}>Moderate: </span>
              <span style={{ color: colors.text }}>0.4 ≤ |r| &lt; 0.6</span>
            </div>
            <div>
              <span style={{ color: colors.textSecondary }}>Weak: </span>
              <span style={{ color: colors.text }}>0.2 ≤ |r| &lt; 0.4</span>
            </div>
            <div>
              <span style={{ color: colors.textSecondary }}>Very Weak: </span>
              <span style={{ color: colors.text }}>|r| &lt; 0.2</span>
            </div>
            <div>
              <span style={{ color: '#26C6DA' }}>Cyan = Positive</span>
              <span style={{ margin: '0 8px' }}>|</span>
              <span style={{ color: '#EF5350' }}>Red = Negative</span>
            </div>
          </div>
        </div>
        </AnimatedCard>
      )}
    </div>
  );
};

export default CorrelationMatrix;